import { RetiredMarker } from '@/components/compliance/RetiredMarker';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronDown, Search, Lock, Layers, Columns3, Download, Plus, Minus, X,
  User, Paperclip, Radio, FileText, Grid2x2, CircleCheck, ShieldCheck, Library,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTeamMembers } from '@/api/hooks/useSettings';
import { BulkPeopleBar } from '@/components/compliance/BulkPeopleBar';
import { BulkEvidenceBar } from '@/components/compliance/BulkEvidenceBar';
import { BulkReviewBar } from '@/components/compliance/BulkReviewBar';
import { ReviewDueChip } from '@/components/compliance/review/ReviewDueChip';
import { Skeleton } from '@/components/ui/skeleton';
import {
  CTS_WORKFLOW_LABEL, CTS_REVIEW_LABEL_TEXT, CTS_REVIEW_CADENCE_LABEL, CTS_ASSESSMENT_IN_PROGRESS_LABEL,
  ctsReviewCadenceWithSource,
} from '@grc/shared';
import { formatCalendarDate } from '@/lib/calendar-date';
import { useControlsWorkspace } from '@/api/hooks/useCtsWorkspace';
import type { WorkspaceList } from '@/api/types/cts-workspace.types';
import {
  buildSections, matches, allControls, toCsv, postureOf, workflowOf, POSTURE_BANDS,
  type FilterKey, type FlatControl,
} from '@/features/compliance/workspace/workspace-filters';

/**
 * CONTROLS WORKSPACE — Tenant Convergence TC2, built to Design's pass.
 *
 * ⚠ THIS SUPERSEDES the mcc_/cmpl_ ControlsPage, which is retired at TC6 and runs in parallel
 * until then. That page was not under-built: it documented, in its own header, that workflow,
 * evidence and policy links were unreachable because the assignment and evidence models hung off
 * `pol_controls` while the catalogue spine was `mcc_`, with zero control-level overlap — and it
 * refused to render an empty Evidence card rather than assert a capability that did not exist.
 * `cts_control_state` is the key that was missing.
 *
 * ⚠ WORKFLOW AND POSTURE ARE TWO COLUMNS BECAUSE THEY ARE TWO AXES. "In Progress · Partially met"
 * is a correct row, not an inconsistency to reconcile. TC1 gave the two tables no column for the
 * other's answer precisely so neither could quietly become a function of the other, and a single
 * merged "status" column would undo that at the surface.
 *
 * ⚠ PLACEHOLDERS ARE MARKED AS PLACEHOLDERS. Add Control, Columns, the policy-document indicator
 * and bulk actions are drawn because Design placed them, and are inert with an explicit title
 * saying so. None of them is wired to anything that could half-work.
 */

const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.11em] text-text-secondary';

// ⚠ CAPR S2 — keyed by the DERIVED status (postureOf), not the deprecated legacy result.
const POSTURE_TONE: Record<string, string> = {
  implemented: 'text-cert-valid-fg',
  partially_implemented: 'text-cert-warn-fg',
  not_implemented: 'text-cert-expired-fg',
  not_yet_assessed: 'text-text-faint italic',
  not_applicable: 'text-text-secondary',
  unknown: 'text-text-secondary',
};
const POSTURE_DOT: Record<string, string> = {
  implemented: 'bg-cert-valid-fg', partially_implemented: 'bg-cert-warn-fg', not_implemented: 'bg-cert-expired-fg',
  not_yet_assessed: 'bg-text-faint/60', not_applicable: 'bg-text-faint/60', unknown: 'bg-text-faint/60',
};
const WORKFLOW_DOT: Record<string, string> = {
  unassigned: 'bg-text-faint/70', assigned: 'bg-sky-400', in_progress: 'bg-blue-400',
  submitted_for_review: 'bg-violet-400', returned: 'bg-amber-400', approved: 'bg-teal-400',
  completed: 'bg-emerald-400', reopened: 'bg-orange-400',
};

// ⚠ CAPR S3 — the review label's tone and rail dot. Overdue is the one red; due soon the amber;
//   "not yet assessed" and "no scheduled review" are states, not failures, and read as muted.
const REVIEW_TONE: Record<string, string> = {
  overdue: 'text-cert-expired-fg', due_soon: 'text-cert-warn-fg', current: 'text-cert-valid-fg',
  not_yet_assessed: 'text-text-faint italic', no_scheduled_review: 'text-text-secondary',
};
const REVIEW_DOT: Record<string, string> = {
  overdue: 'bg-cert-expired-fg', due_soon: 'bg-cert-warn-fg', current: 'bg-cert-valid-fg',
  not_yet_assessed: 'bg-text-faint/60', no_scheduled_review: 'bg-text-faint/60',
};

const postureLabel = (v: string) =>
  POSTURE_BANDS.find((b) => b.value === v)?.label ?? v.replace(/_/g, ' ');

// ── The per-row indicator cluster ─────────────────────────────────────────────────────────────

/**
 * Six at-a-glance indicators.
 *
 * ⚠ THE SLOT ORDER IS FIXED AND THE GLYPH IS THE MEANING. Each indicator lives at a constant
 * position and carries a distinct outline icon, because a row of identical marks reduces six facts
 * to one undifferentiated smear — the eye can count lit marks but cannot tell WHICH is lit. Reading
 * a row at a glance depends on position AND glyph agreeing, so neither may be changed casually.
 *
 * ⚠ AND A DIM ICON MEANS "NOTHING RECORDED", NEVER "WE DID NOT LOOK". The policy slot is
 * permanently muted and says why on hover: there is no link between a `ccl_` catalogue control and
 * a tenant policy document, so lighting it would be the empty-Evidence-card mistake in miniature.
 */
const IND = {
  /** Present / satisfied. */
  on: 'text-[#3ecf8e]',
  /** Needs attention — a real state, not a soft failure: something is mid-flight or came back. */
  warn: 'text-[#f5b74d]',
  /** Absent. */
  off: 'text-[#333c4b]',
} as const;

function Indicators({ c }: { c: FlatControl }) {
  const wf = workflowOf(c);
  const items: { Icon: typeof User; tone: keyof typeof IND; title: string }[] = [
    {
      Icon: User, tone: c.owner_name ? 'on' : 'off',
      title: c.owner_name ? `Responsible — ${c.owner_name}` : 'Responsible — nobody',
    },
    {
      // ⚠ Evidence PAST THE CATALOGUE'S RECOMMENDED CADENCE is not "present" and not "absent" — it
      //    is the clearest needs-attention case on the row, so it takes the amber rather than a
      //    green that would report an ageing capture as freshly satisfied.
      // ⚠ P1.4: this used to say EXPIRED. The go-forward model has no validity window; the band
      //    measures age against the catalogue's ADVICE, which is a weaker claim and must read as one.
      Icon: Paperclip,
      tone: c.evidence_count === 0 ? 'off' : c.evidence_stale > 0 ? 'warn' : 'on',
      title: c.evidence_count === 0 ? 'Evidence attached — none'
        // ⚠ P1.4: the band counts artifacts PAST THE CATALOGUE'S RECOMMENDED CADENCE — it never
        //    measured expiry, and after the repoint there is no validity window to expire.
        : c.evidence_stale > 0 ? `Evidence attached — ${c.evidence_stale} past recommended cadence`
          : `Evidence attached — ${c.evidence_count}`,
    },
    {
      Icon: Radio, tone: c.implementation_status !== null ? 'on' : 'off',
      title: c.implementation_status !== null ? 'Monitoring — assessment approved' : 'Monitoring — not yet assessed',
    },
    {
      // PLACEHOLDER SLOT — kept in position so the six never shift, muted because no such link
      // exists to report on.
      Icon: FileText, tone: 'off',
      title: 'Policy document linked — policy linking is not available in this release',
    },
    {
      Icon: Grid2x2, tone: c.mappings.length > 0 ? 'on' : 'off',
      title: c.mappings.length > 0 ? `Framework mapping — ${c.mappings.length}` : 'Framework mapping — none',
    },
    {
      Icon: CircleCheck,
      tone: wf === 'approved' || wf === 'completed' ? 'on'
        : wf === 'submitted_for_review' || wf === 'returned' || wf === 'reopened' ? 'warn' : 'off',
      title: wf === 'approved' || wf === 'completed' ? 'Approval — approved'
        : wf === 'submitted_for_review' ? 'Approval — submitted for review'
          : wf === 'returned' ? 'Approval — returned for changes'
            : wf === 'reopened' ? 'Approval — reopened'
              : 'Approval — not submitted',
    },
  ];
  return (
    <span className="hidden shrink-0 items-center gap-[11px] lg:flex">
      {items.map(({ Icon, tone, title }, i) => (
        <span key={i} title={title} className="inline-flex">
          <Icon aria-hidden="true" strokeWidth={1.3} className={cn('h-[14px] w-[14px]', IND[tone])} />
        </span>
      ))}
    </span>
  );
}

// ── Selection + bulk actions ──────────────────────────────────────────────────────────────────

/**
 * The bulk action bar.
 *
 * ⚠ TWO ACTIONS ARE NOW REAL, AND TWO ARE STILL PLACEHOLDERS FOR DIFFERENT REASONS.
 *
 *  · ⚠ "Assign to me" is LIVE — the tenant's first touch. Until TC6 the new model had no create
 *    path at all: every `cts_control_state` row in the api came from a test fixture, so a tenant
 *    could adopt a framework, see its controls, and do nothing with them. Assigning ensures the
 *    control's state and then creates the work.
 *  · ⚠ "Remove from workspace" is LIVE. TC2 recorded exactly what was missing — "`cts_control_state`
 *    has no `removed_at`/`deactivated_at` column" — and V1043 supplied it. The distinction TC2
 *    drew still holds and is now enforced by the schema: removal is NOT
 *    `applicability = 'not_applicable'` (this control does not apply to us, which demands a
 *    rationale), and NOT a posture. Three facts, three fields.
 *  · "Assign owner" writes `cts_control_state.responsible_user_id` — a DIFFERENT act from creating
 *    an assignment, and deliberately not folded into the one above. Its write path is unbuilt.
 *  · "Request approval" is a WORKFLOW TRANSITION on an ASSIGNMENT, not on a control. The machine
 *    exists (TC4); what a bulk control-selection means for it does not.
 *
 * The reminder line still states the rule the removal obeys: it deactivates and keeps everything.
 */
// ── Add Control ───────────────────────────────────────────────────────────────────────────────

/**
 * ⚠ THE BUTTON OPENS A CHOOSER, IT DOES NOT CREATE A CONTROL — and that is architecture, not a
 * design preference. The catalogue is platform-authored: `ccl_controls` carries
 * `CHECK (tenant_id IS NULL)`, so a tenant-owned row in it is not merely unimplemented, it is
 * unrepresentable. A tenant reaches new controls by ADOPTING published content, which is why the
 * three paths below are all "choose", never "author into the catalogue".
 */
function AddControlModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  // Escape and click-outside both dismiss; focus moves into the dialog on open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const Option = ({
    Icon, title, body, badge, onPick, why,
  }: {
    Icon: typeof ShieldCheck; title: string; body: string;
    badge?: string; onPick?: () => void; why?: string;
  }) => (
    <button
      type="button"
      onClick={onPick}
      disabled={!onPick}
      title={onPick ? undefined : `Placeholder — ${why}`}
      className={cn(
        'flex w-full items-center gap-[13px] rounded-[9px] border border-cert-neutral-border bg-bg-surface px-[14px] py-[13px] text-left transition-colors',
        onPick ? 'hover:border-accent-cyan/60 hover:bg-bg-surface-2' : 'opacity-60',
      )}
    >
      <span className="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[8px] bg-bg-surface-2">
        <Icon className="h-[15px] w-[15px] text-accent-cyan" strokeWidth={1.5} aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-[8px]">
          <span className="text-[13px] font-medium text-text-primary">{title}</span>
          {badge && (
            <span className="rounded-[4px] bg-accent-cyan/15 px-[6px] py-[1px] text-[10px] font-semibold uppercase tracking-[0.06em] text-accent-cyan">
              {badge}
            </span>
          )}
        </span>
        <span className="mt-[3px] block text-[12px] leading-[1.45] text-text-secondary">{body}</span>
      </span>
      <ChevronRight className="h-[15px] w-[15px] shrink-0 text-text-faint" aria-hidden="true" />
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/55 p-[24px] pt-[12vh]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="add-control-title"
        className="w-full max-w-[540px] rounded-[12px] border border-cert-neutral-border bg-bg-base p-[20px] shadow-2xl outline-none"
      >
        <div className="mb-[6px] flex items-start gap-[12px]">
          <h2 id="add-control-title" className="flex-1 text-[16px] font-semibold text-text-primary">Add Control</h2>
          <button type="button" onClick={onClose} aria-label="Close"
            className="text-text-faint transition-colors hover:text-text-primary">
            <X className="h-[16px] w-[16px]" aria-hidden="true" />
          </button>
        </div>
        <p className="mb-[16px] text-[12.5px] leading-[1.5] text-text-secondary">
          Controls come from the published library — choose how you want to add them.
        </p>
        <div className="space-y-[9px]">
          {/*
            ⚠ ROUTES TO THE TC3 FRAMEWORKS WORKSPACE, NOT THE LEGACY /compliance PAGE.
            This shipped pointing at `/compliance` — the pol_-based page that runs in parallel
            until TC6 — so a brand-new affordance handed the tenant to the surface it supersedes,
            where activating does something different to a different model. A destination that
            exists but is the wrong one is worse than a dead link: nothing looks broken, so nobody
            reports it. The Add-Control chooser's whole job is to route to where the action lives.
          */}
          <Option
            Icon={ShieldCheck}
            title="Activate a framework"
            body="Turn on a framework you are licensed for. Its controls join your workspace with any work you have already done on them."
            onPick={() => { onClose(); navigate('/compliance/workspace/frameworks'); }}
          />
          {/* ⚠ REAL NOW, and the `why` string went WITH the wiring. It said "library browsing
              beyond your adopted frameworks lands in a later slice" — true when written, false the
              moment this page existed, and a stale explanation is worse than none. */}
          <Option
            Icon={Library} badge="Recommended"
            title="Add control from library"
            body="Browse the published VeriGRC Control Library and add individual controls to your workspace."
            onPick={() => { onClose(); navigate('/compliance/control-library'); }}
          />
          {/*
            ⚠ "Create custom control" was REMOVED, not greyed (D-TC-NO-TENANT-CUSTOM-CONTROL-FOR-NOW).
            An inert affordance IMPLIES a capability: a tenant who plans around "we can author our
            own controls" has been misled by a disabled button, and greyed reads as "coming soon"
            rather than "not offered". `ccl_controls` carries CHECK (tenant_id IS NULL), so a
            tenant-owned catalogue row is unrepresentable — and if it is ever built it gets its own
            table rather than a relaxation of that invariant. Re-designed properly IF a real need
            appears; until then the chooser offers only what the product actually does.
          */}
        </div>
      </div>
    </div>
  );
}

// ── The left filter rail ──────────────────────────────────────────────────────────────────────

function Rail({
  data, active, toggle,
}: { data: WorkspaceList; active: Set<FilterKey>; toggle: (k: FilterKey) => void }) {
  const sections = useMemo(() => buildSections(data), [data]);
  return (
    <nav aria-label="Filter controls" className="w-[196px] shrink-0 space-y-[22px] pr-[8px]">
      {sections.map((s) => (
        <div key={s.id}>
          <div className="mb-[7px] flex items-baseline justify-between">
            <span className={EYEBROW}>{s.title}</span>
            {s.caption && <span className="text-[9px] uppercase tracking-[0.11em] text-text-faint">{s.caption}</span>}
          </div>
          <ul className="space-y-[1px]">
            {s.bands.map((b) => {
              const on = active.has(b.key);
              return (
                <li key={b.key}>
                  <button
                    type="button"
                    onClick={() => toggle(b.key)}
                    aria-pressed={on}
                    className={cn(
                      'flex w-full items-center gap-[8px] rounded-[5px] px-[8px] py-[5px] text-left text-[12px] transition-colors',
                      on ? 'bg-accent-cyan/12 text-accent-cyan' : 'text-text-secondary hover:bg-bg-surface-2',
                    )}
                  >
                    {(s.id === 'posture' || s.id === 'workflow' || s.id === 'review') && (
                      <span className={cn('h-[6px] w-[6px] shrink-0 rounded-full',
                        s.id === 'posture' ? POSTURE_DOT[b.key.split(':')[1]]
                          : s.id === 'review' ? REVIEW_DOT[b.key.split(':')[1]]
                          : WORKFLOW_DOT[b.key.split(':')[1]])} />
                    )}
                    <span className="min-w-0 flex-1 truncate">{b.label}</span>
                    {/*
                      ⚠ A ZERO IS PRINTED, NOT HIDDEN. In the Workflow section the eight ruled
                      states always render; the three the database cannot yet produce show 0.
                      Hiding them would misrepresent the vocabulary as five states; fabricating a
                      number would be a lie. Zero also means this rail does not change SHAPE when
                      TC4 lands — the states simply start filling.
                    */}
                    <span className={cn('shrink-0 text-[11px] tabular-nums',
                      b.count === 0 ? 'text-text-faint/50' : on ? 'text-accent-cyan' : 'text-text-faint')}>
                      {b.count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────────────────────

export function ControlsWorkspacePage() {
  const { data, isLoading, isError } = useControlsWorkspace();
  const [q, setQ] = useState('');
  const [active, setActive] = useState<Set<FilterKey>>(new Set());
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const { data: team } = useTeamMembers();
  const [addOpen, setAddOpen] = useState(false);

  const toggle = (k: FilterKey) => setActive((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const visible = useMemo<FlatControl[]>(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return allControls(data).filter((c) => {
      if (!matches(c, active)) return false;
      if (!needle) return true;
      return c.display_code.toLowerCase().includes(needle)
        || c.title.toLowerCase().includes(needle)
        || c.mappings.some((m) =>
          (m.framework_name ?? '').toLowerCase().includes(needle)
          || (m.requirement_display_code ?? '').toLowerCase().includes(needle)
          || (m.external_ref_id ?? '').toLowerCase().includes(needle));
    });
  }, [data, q, active]);

  /**
   * ⚠ SELECTION IS DERIVED AGAINST THE VISIBLE SET, NOT SYNCHRONISED TO IT.
   *
   * A bulk action must never reach a row the tenant cannot see. Rather than clearing `picked` in an
   * effect whenever the filters change — which leaves a committed render where the selection and
   * the table disagree, and is exactly the bug that took O2 red in CI — the effective selection is
   * INTERSECTED with what is on screen on every render. Changing a filter therefore rescopes the
   * selection in the same paint as the rows, and a hidden row is unreachable by construction rather
   * than by remembering to clear it.
   */
  const visibleIds = useMemo(() => new Set(visible.map((c) => c.control_reference)), [visible]);
  const selected = useMemo(
    () => [...picked].filter((id) => visibleIds.has(id)),
    [picked, visibleIds],
  );
  const allShown = visible.length > 0 && selected.length === visible.length;

  const toggleRow = (id: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  /** Select-all covers exactly the CURRENTLY VISIBLE (filtered) rows — never the whole catalogue. */
  const toggleAll = () => setPicked(allShown ? new Set() : new Set(visibleIds));

  /** Re-group the surviving rows so the table always reflects the filters exactly. */
  const groups = useMemo(() => {
    const out: { domain: string; families: { family: string; rows: FlatControl[] }[]; n: number }[] = [];
    for (const c of visible) {
      let d = out[out.length - 1];
      if (!d || d.domain !== c._domain) { d = { domain: c._domain, families: [], n: 0 }; out.push(d); }
      let f = d.families[d.families.length - 1];
      if (!f || f.family !== c._family) { f = { family: c._family, rows: [] }; d.families.push(f); }
      f.rows.push(c); d.n += 1;
    }
    return out;
  }, [visible]);

  const exportCsv = () => {
    // ⚠ Exports the ROWS ON SCREEN, so the file can never disagree with the view it was taken
    //    from — the failure where a tenant sends an auditor rows they had filtered out.
    const blob = new Blob([toCsv(visible)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'controls.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return <div className="space-y-[12px] p-[24px]"><Skeleton className="h-[28px] w-[220px]" /><Skeleton className="h-[300px] w-full" /></div>;
  }
  if (isError || !data) {
    return <div className="p-[24px] text-[13px] text-text-secondary">Controls could not be loaded.</div>;
  }

  const { adopted, gated_adoptions: gated, control_count: total } = data;

  return (
    <div className="p-[24px]">
      <header className="mb-[18px] flex flex-wrap items-start gap-[16px]">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold tracking-[-0.01em] text-text-primary">Controls</h1>
          <p className="mt-[4px] text-[13px] text-text-secondary">
            Your control workspace across your active compliance programs. Each control appears
            once, with its applicable framework mappings.
          </p>
        </div>
        {/*
          ⚠ Opens a CHOOSER, never a create form. The catalogue is platform-authored
          (`ccl_controls` CHECK (tenant_id IS NULL)), so a tenant reaches new controls by adopting
          published content — the modal makes that the visible shape of the action.
        */}
        <button
          type="button" onClick={() => setAddOpen(true)}
          className="flex items-center gap-[6px] rounded-[7px] bg-accent-cyan px-[14px] py-[8px] text-[13px] font-medium text-white transition-opacity hover:opacity-90"
        >
          <Plus className="h-[14px] w-[14px]" aria-hidden="true" /> Add Control
        </button>
      </header>

      {gated.length > 0 && (
        <div className={cn(PANEL, 'mb-[14px] flex items-start gap-[10px] px-[16px] py-[12px]')}>
          <Lock className="mt-[2px] h-[14px] w-[14px] shrink-0 text-cert-warn-fg" aria-hidden="true" />
          <p className="text-[12px] text-text-secondary">
            <span className="font-medium text-text-primary">{gated.map((g) => g.name).join(', ')}</span>{' '}
            {gated.length === 1 ? 'is in your workspace but its licence is not currently active'
              : 'are in your workspace but their licences are not currently active'}
            , so their content is not shown. Your work on these controls is unaffected and returns
            when the licence does.
          </p>
        </div>
      )}

      <div className="flex gap-[20px]">
        <Rail data={data} active={active} toggle={toggle} />

        <div className="min-w-0 flex-1">
          <div className="mb-[12px] flex flex-wrap items-center gap-[10px]">
            <div className="relative min-w-[260px] flex-1">
              <Search className="pointer-events-none absolute left-[11px] top-1/2 h-[14px] w-[14px] -translate-y-1/2 text-text-faint" aria-hidden="true" />
              <input
                type="search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search control ID, name, description, or framework requirement…"
                aria-label="Search controls"
                className="w-full rounded-[8px] border border-cert-neutral-border bg-bg-surface py-[9px] pl-[32px] pr-[12px] text-[13px] text-text-primary placeholder:text-text-faint focus:border-accent-cyan focus:outline-none"
              />
            </div>
            <button
              type="button" disabled
              title="Placeholder — column selection is not wired in this release"
              className="flex items-center gap-[6px] rounded-[8px] border border-cert-neutral-border bg-bg-surface px-[12px] py-[8px] text-[12px] text-text-secondary opacity-55"
            >
              <Columns3 className="h-[13px] w-[13px]" aria-hidden="true" /> Columns
            </button>
            <button
              type="button" onClick={exportCsv}
              title="Exports exactly the rows shown, with your active filters applied"
              className="flex items-center gap-[6px] rounded-[8px] border border-cert-neutral-border bg-bg-surface px-[12px] py-[8px] text-[12px] text-text-secondary transition-colors hover:text-text-primary"
            >
              <Download className="h-[13px] w-[13px]" aria-hidden="true" /> Export CSV
            </button>
          </div>

          <div className="mb-[8px] flex items-center justify-end gap-[10px] text-[11px] text-text-secondary">
            <button type="button" onClick={() => setCollapsed(new Set())} className="flex items-center gap-[4px] hover:text-text-primary">
              <Plus className="h-[11px] w-[11px]" aria-hidden="true" /> Expand all
            </button>
            <span className="text-text-faint">·</span>
            <button type="button" onClick={() => setCollapsed(new Set(groups.map((g) => g.domain)))} className="flex items-center gap-[4px] hover:text-text-primary">
              <Minus className="h-[11px] w-[11px]" aria-hidden="true" /> Collapse all
            </button>
          </div>

          {/*
            ⚠ ALWAYS RENDERED — the bar no longer appears on selection. It is disabled at zero with
            a zero-state that says what selecting is for; an affordance that only exists once you
            have already discovered it teaches nobody.
          */}
          <BulkPeopleBar
            selected={selected}
            /* ⚠ `visible`, not the whole catalogue: a bulk action must never reach a row a filter
               is hiding, and the confirm count must describe the same set. */
            rows={visible}
            team={team}
            onClear={() => setPicked(new Set())}
          />
          {/* ⚠ P1.2 — the evidence actions, as a SIBLING bar over the SAME visible selection. */}
          <BulkEvidenceBar selected={selected} rows={visible} onClear={() => setPicked(new Set())} />
          {/* ⚠ CAPR S5 — the review actions (cadence, re-assessment start), over the SAME visible selection. */}
          <BulkReviewBar selected={selected} rows={visible} team={team} onClear={() => setPicked(new Set())} />

          <div className={cn(PANEL, 'overflow-hidden')}>
            <div className="flex items-center gap-[14px] border-b border-border-subtle px-[16px] py-[9px]">
              <input
                type="checkbox" checked={allShown} onChange={toggleAll}
                aria-label={allShown ? 'Clear selection' : 'Select all visible controls'}
                className="h-[13px] w-[13px] shrink-0 accent-[#009FFD]"
              />
              <span className={cn(EYEBROW, 'min-w-0 flex-1')}>Control</span>
              {/* ⚠ "Responsible", from the locked vocabulary. This column reads `owner_name`, which is the
                  PROJECTION ALIAS for responsible_user_id — the alias is an identifier and stays
                  (B-RACI-CONTRACT-FIELD-RENAME); only the rendered word moves. */}
              <span className={cn(EYEBROW, 'hidden w-[120px] shrink-0 md:block')}>Responsible</span>
              <span className={cn(EYEBROW, 'w-[104px] shrink-0')}>Posture</span>
              <span className={cn(EYEBROW, 'hidden w-[140px] shrink-0 sm:block')}>Workflow</span>
              <span className={cn(EYEBROW, 'hidden w-[104px] shrink-0 lg:block')}>Assessed</span>
              {/* ⚠ CAPR S3 — the review schedule: label (with open work beside it, never instead of it),
                  next review date, and the cadence with where it came from. */}
              <span className={cn(EYEBROW, 'hidden w-[124px] shrink-0 md:block')}>Review</span>
              <span className={cn(EYEBROW, 'hidden w-[96px] shrink-0 xl:block')}>Next review</span>
              <span className={cn(EYEBROW, 'hidden w-[96px] shrink-0 xl:block')}>Cadence</span>
              <span className={cn(EYEBROW, 'hidden w-[62px] shrink-0 lg:block')}>Evidence</span>
              <span className="w-[14px] shrink-0" />
            </div>

            {groups.length === 0 ? (
              <div className="flex items-start gap-[10px] px-[16px] py-[18px]">
                <Layers className="mt-[2px] h-[15px] w-[15px] shrink-0 text-text-faint" aria-hidden="true" />
                {/*
                  ⚠ FOUR DIFFERENT EMPTIES, FOUR DIFFERENT MESSAGES. Nothing adopted · everything
                  licence-gated · filtered to nothing · searched to nothing all produce zero rows
                  and are entirely different situations. One blank table for all four would hide
                  the only ones the tenant can act on.
                */}
                <p className="text-[13px] text-text-secondary">
                  {total === 0
                    ? (gated.length > 0
                      ? 'Every framework in your workspace currently has an inactive licence, so no control content can be shown.'
                      : 'No frameworks have been added to your workspace yet, so there are no controls to work on.')
                    : (active.size > 0 || q
                      ? 'No controls match the current filters.'
                      : 'No controls to show.')}
                </p>
              </div>
            ) : groups.map((g) => {
              const open = !collapsed.has(g.domain);
              return (
                <section key={g.domain}>
                  <button
                    type="button" aria-expanded={open}
                    onClick={() => setCollapsed((p) => {
                      const n = new Set(p); if (n.has(g.domain)) n.delete(g.domain); else n.add(g.domain); return n;
                    })}
                    className="flex w-full items-center gap-[9px] border-b border-border-subtle bg-bg-surface-2/30 px-[16px] py-[9px] text-left"
                  >
                    {open ? <ChevronDown className="h-[13px] w-[13px] text-text-secondary" aria-hidden="true" />
                      : <ChevronRight className="h-[13px] w-[13px] text-text-secondary" aria-hidden="true" />}
                    <span className="text-[13px] font-semibold text-text-primary">{g.domain}</span>
                    <span className="ml-auto text-[11px] text-text-secondary">{g.n} controls</span>
                  </button>
                  {open && g.families.map((f) => (
                    <div key={f.family}>
                      <div className="border-b border-border-subtle px-[16px] py-[5px]">
                        <span className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-text-faint">
                          {f.family}
                        </span>
                      </div>
                      {f.rows.map((c) => {
                        const posture = postureOf(c);
                        const wf = workflowOf(c);
                        const assessed = formatCalendarDate(c.last_approved_date);
                        const nextReview = formatCalendarDate(c.next_review_date);
                        return (
                          <div
                            key={c.control_reference}
                            className="flex items-center gap-[14px] border-b border-border-subtle px-[16px] transition-colors last:border-b-0 hover:bg-bg-surface-2"
                          >
                            {/*
                              ⚠ THE CHECKBOX IS A SIBLING OF THE LINK, NOT A CHILD OF IT.
                              Nesting a control inside an anchor is invalid HTML and leaves the
                              behaviour depending on a stopPropagation call that any later edit can
                              drop. As siblings, selecting cannot navigate — there is no event to
                              stop — and the rest of the row still opens the hub.
                            */}
                            <input
                              type="checkbox"
                              checked={selected.includes(c.control_reference)}
                              onChange={() => toggleRow(c.control_reference)}
                              aria-label={`Select ${c.display_code}`}
                              className="h-[13px] w-[13px] shrink-0 accent-[#009FFD]"
                            />
                          <Link
                            to={`/compliance/workspace/controls/${c.control_reference}`}
                            className="flex min-w-0 flex-1 items-center gap-[14px] py-[11px] focus-visible:outline-none"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="flex items-baseline gap-[10px]">
                                <span className="font-mono text-[12px] text-accent-cyan">{c.display_code}</span>
                                {/* ⚠ AHEAD of the title, which truncates — a fact that can be
                                    truncated off the row is not a fact the surface states. */}
                                <RetiredMarker retiredAt={c.retired_at} />
                                {/* ⚠ CAPR S5 — the server's review label as a chip, ahead of the
                                    truncating title (the Review column hides below md). */}
                                <ReviewDueChip label={c.review_label} />
                                <span className="truncate text-[13px] font-medium text-text-primary">{c.title}</span>
                              </span>
                              <span className="mt-[3px] flex flex-wrap items-center gap-[6px]">
                                {[...new Set(c.mappings.map((m) => m.framework_name).filter((n): n is string => n !== null))]
                                  .map((n) => (
                                    <span key={n} className="rounded-[4px] bg-bg-surface-2 px-[7px] py-[1px] font-mono text-[10px] text-text-secondary">{n}</span>
                                  ))}
                                {/*
                                  ⚠ A CITED EDGE SHOWS ITS RAW REFERENCE AND NO NAME — the absence
                                  IS the property. There is no framework row to resolve; naming it
                                  would need an mcc_ join this module has never made. O4 verified
                                  this on the one live cited edge in production, and it is the case
                                  where the CORRECT result looks worse than the incorrect one.
                                */}
                                {c.mappings.filter((m) => m.external_ref_id !== null).map((m) => (
                                  <span key={m.external_ref_id}
                                    title="External reference — this platform holds no content for that framework"
                                    className="rounded-[4px] border border-dashed border-cert-neutral-border px-[7px] py-[1px] font-mono text-[10px] text-text-faint">
                                    {m.external_ref_id}
                                  </span>
                                ))}
                              </span>
                            </span>
                            <Indicators c={c} />
                            <span className={cn('hidden w-[120px] shrink-0 truncate text-[12px] md:block',
                              c.owner_name ? 'text-text-secondary' : 'text-text-faint')}>
                              {/* "Not set", matching the control hub — two words for one state
                                  reads as two different states. */}
                              {c.owner_name ?? 'Not set'}
                            </span>
                            <span className={cn('w-[104px] shrink-0 text-[12px] font-medium', POSTURE_TONE[posture] ?? 'text-text-secondary')}>
                              {postureLabel(posture)}
                            </span>
                            <span className="hidden w-[140px] shrink-0 sm:block">
                              <span className="inline-flex items-center gap-[6px] rounded-full bg-bg-surface-2 px-[9px] py-[3px] text-[11px] text-text-secondary">
                                <span className={cn('h-[5px] w-[5px] rounded-full', wf ? WORKFLOW_DOT[wf] : 'bg-text-faint/60')} />
                                {wf ? CTS_WORKFLOW_LABEL[wf] : (c.workflow_status ?? 'Unassigned')}
                              </span>
                            </span>
                            <span className={cn('hidden w-[104px] shrink-0 text-[11px] lg:block',
                              assessed ? 'text-text-secondary' : 'text-text-faint')}>
                              {assessed ?? 'Never assessed'}
                            </span>
                            <span className="hidden w-[124px] shrink-0 md:block" data-testid={`review-${c.control_reference}`}>
                              <span className={cn('block text-[12px] font-medium', REVIEW_TONE[c.review_label] ?? 'text-text-secondary')}>
                                {CTS_REVIEW_LABEL_TEXT[c.review_label] ?? c.review_label}
                              </span>
                              {/* ⚠ TWO FACTS, BOTH SHOWN: open work never replaces the overdue state. */}
                              {c.in_progress && (
                                <span className="block text-[10.5px] text-text-secondary">{CTS_ASSESSMENT_IN_PROGRESS_LABEL}</span>
                              )}
                            </span>
                            <span className={cn('hidden w-[96px] shrink-0 text-[11px] xl:block',
                              nextReview ? 'text-text-secondary' : 'text-text-faint')}>
                              {nextReview ?? '—'}
                            </span>
                            <span className="hidden w-[96px] shrink-0 truncate text-[11px] text-text-secondary xl:block"
                              title={ctsReviewCadenceWithSource(c.effective_cadence, c.cadence_source)}>
                              {CTS_REVIEW_CADENCE_LABEL[c.effective_cadence] ?? c.effective_cadence}
                              {c.cadence_source === 'override' && <span className="text-text-faint"> · override</span>}
                            </span>
                            <span className={cn('hidden w-[62px] shrink-0 text-[11px] lg:block',
                              c.evidence_count > 0 ? 'text-text-secondary' : 'text-text-faint')}>
                              {c.evidence_count > 0 ? c.evidence_count : '—'}
                            </span>
                            <ChevronRight className="h-[14px] w-[14px] shrink-0 text-text-faint" aria-hidden="true" />
                          </Link>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </section>
              );
            })}
          </div>

          {adopted.length > 0 && (
            <p className="mt-[10px] text-[11px] text-text-faint">
              Showing {visible.length} of {total} controls across {adopted.map((a) => a.name).join(', ')}.
            </p>
          )}
        </div>
      </div>

      {addOpen && <AddControlModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}
